import { useRef } from 'react';

/** Minimum horizontal travel (px) before a touch counts as a swipe. */
const SWIPE_MIN_X = 48;
/** Horizontal travel must dominate vertical by this factor, else it's a scroll. */
const SWIPE_DOMINANCE = 2;
/** Longest touch (ms) that still reads as a flick rather than a slow drag. */
const SWIPE_MAX_MS = 600;

export interface SwipeNavHandlers {
  onTouchStart: (event: React.TouchEvent) => void;
  onTouchEnd: (event: React.TouchEvent) => void;
}

interface TouchOrigin {
  x: number;
  y: number;
  at: number;
}

/**
 * Horizontal swipe detection for page-level navigation, as spreadable touch
 * handlers. Deliberately conservative so it never fights native gestures: only
 * single-finger touches count, the travel must be mostly horizontal (a
 * vertical scroll with some drift stays a scroll), it must clear a real
 * distance, and it must be quick — a slow drag (text selection, a hesitating
 * thumb) never navigates. Passing null for a direction disables it (at the
 * newest/oldest day the swipe simply does nothing).
 */
export function useSwipeNav(
  onSwipeLeft: (() => void) | null,
  onSwipeRight: (() => void) | null
): SwipeNavHandlers {
  const originRef = useRef<TouchOrigin | null>(null);

  const onTouchStart = (event: React.TouchEvent) => {
    // A second finger means pinch/zoom territory — opt out of the gesture.
    if (event.touches.length !== 1) {
      originRef.current = null;
      return;
    }
    const touch = event.touches[0];
    originRef.current = { x: touch.clientX, y: touch.clientY, at: performance.now() };
  };

  const onTouchEnd = (event: React.TouchEvent) => {
    const origin = originRef.current;
    originRef.current = null;
    if (!origin || event.touches.length > 0) return;
    if (performance.now() - origin.at > SWIPE_MAX_MS) return;
    const touch = event.changedTouches[0];
    if (!touch) return;
    const dx = touch.clientX - origin.x;
    const dy = touch.clientY - origin.y;
    if (Math.abs(dx) < SWIPE_MIN_X || Math.abs(dx) < Math.abs(dy) * SWIPE_DOMINANCE) return;
    if (dx < 0) onSwipeLeft?.();
    else onSwipeRight?.();
  };

  return { onTouchStart, onTouchEnd };
}
